(ns app.front.view.conditions.common
  (:require [app.domain.application.condition.model :as condition-model]
            [app.domain.group.config.model :as config-model]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.add-widget :as ui-add]
            [app.front.ui.chevron :as c]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.separator :as ui-sep]
            [app.front.util :as futil]
            [app.front.view.workspace.subs :as ws-subs]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(rf/reg-sub ::conditions-enabled-for-group?
  (fn [[_ group-id]]
    (rf/subscribe [::entities/entity group-id]))
  :-> condition-model/enabled-for-group?)

(rf/reg-sub ::general-conditions-enabled-for-group?
  (fn [[_ group-id]]
    (rf/subscribe [::entities/entity group-id]))
  (fn [group]
    (and (condition-model/enabled-for-group? group)
         (-> group :group/features (contains? :group.feature/general-conditions)))))

(rf/reg-sub ::default-conditions-enabled-for-group?
  (fn [[_ group-id]]
    (rf/subscribe [::entities/entity group-id]))
  :-> condition-model/enabled-for-group?)

(rf/reg-sub ::condition-due-dates-enabled?
  (fn [[_ group-id]]
    [(rf/subscribe [::entities/entity group-id])])
  (fn [[group]]
    (and (ff/enabled? :feature-flag.id/condition-due-dates)
         (-> group :group/features (contains? :group.feature/condition-due-date)))))

(rf/reg-sub ::condition-due-date-manual-inputs?
  :<- [::ws-subs/workspace]
  (fn [ws _]
    (config-model/resolve-boolean :group.config/condition-due-date-manual-inputs? ws)))

(rf/reg-sub ::conditions
  (fn [[_ parent-id]]
    (rf/subscribe [::entities/entities {:entity/parent parent-id
                                        :entity/type   :entity.type/application-condition}]))
  (fn [conditions]
    (sort-by :ordered/order conditions)))

(rf/reg-sub ::permit-specific-conditions
  (fn [[_ parent-id]]
    (rf/subscribe [::conditions parent-id]))
  (fn [conditions]
    (remove condition-model/general-condition? conditions)))

(rf/reg-sub ::pre-consultation-origin-conditions
  (fn [[_ parent-id]]
    (rf/subscribe [::permit-specific-conditions parent-id]))
  (fn [conditions]
    (filter condition-model/pre-consultation-origin? conditions)))

(rf/reg-sub ::templates-for-group
  (fn [[_ group-id]]
    (rf/subscribe [::entities/entities {:entity/type            :entity.type/application-condition-template
                                        :domain/authority-group group-id}]))
  (fn [templates _]
    (->> templates
         (sort-by :ordered/order))))




(rf/reg-sub ::condition-edit-key
  :-> :condition-edit-key)

;; Common view components and utilities

(def state->border {:condition/draft       "border-status-warning"
                    :condition/open        "border-status-error"
                    :condition/not-cleared "border-status-error"
                    :condition/cleared     "border-status-informative"
                    :condition/overdue     "border-status-warning"
                    :condition/closed      "border-status-success"})


(def approval-result->icon {:approval.condition.result/not-cleared i/remove
                            :approval.condition.result/cleared     i/check})


(def approval-result->color {:approval.condition.result/cleared     "var(--status-success)"
                             :approval.condition.result/not-cleared "var(--status-error)"})



(defn insert-template-fn
  [title-key body-key]
  (fn [condition {:application.condition.template/keys [title body attachment-required?]}]
    (-> condition
        (assoc body-key body)
        (assoc title-key title)
        (assoc :application.condition/attachment-required? (boolean attachment-required?)))))

(defn header-row
  [{:keys [idx title row-count]} & [title-edit & children]]
  (into [:div.flex.bg-blue-100.p-2.rounded-md.mb-2
         [:div.flex-1.leading-6.max-w-min.text-left
          [ui/pill {:text row-count}]]
         [:div.flex-initial.font-bold.leading-9 (when idx (str idx "."))]
         (or title-edit
             [:div.flex-1.ml-1.font-bold.leading-9 title])]
        children))

(defn condition-row
  [{:keys [tr id item index border-color
           augment-target-props augment-draggable-props
           item->title item->body item->state item->group-name
           item->approval item->due-date item->attachment-required?
           item-closed? item->closed-date item->closed-by
           pre-consultation?
           reordering? can-edit? can-close?
           on-edit on-close
           attachments-section attachment-required-but-missing?]}]
  (r/with-let [*expanded? (r/atom false)]
    (let [state (when item->state (item->state item))]
      [:div.grid.grid-cols-3.md:grid-cols-6.gap-4.items-center.mb-2.ml-2.p-4.bg-gray-200.border-l-4.border-solid.leading-snug.hover:bg-ui-blue-10.rounded-md
       (merge {:on-click #(swap! *expanded? not)}
              (if reordering?
                (-> {:class (str border-color " transform ease-in-out duration-100 cursor-pointer")}
                    (augment-draggable-props item)
                    (augment-target-props item))
                {:class border-color}))
       [:div.col-span-3.flex.items-center.select-none
        [c/chevron {:tr tr :expanded? @*expanded? :side :left :size 24}]
        [:span.ml-2
         (str (inc index) ". ")
         (-> item item->title)]]
       (if state
         [:div.font-bold.uppercase (tr [state])]
         [:div])
       (when item->due-date
         (when-let [due-date (item->due-date item)]
           [:div
            [:span.font-bold (tr [:condition/due-date]) ": "]
            [:span (date-util/date due-date)]]))
       [:div.col-span-2.flex.items-center.gap-2
        (if item->group-name
          (-> item item->group-name)
          [:div])
        (when pre-consultation?
          [:span.text-xs.font-semibold.px-2.py-0.5.rounded.bg-blue-200.text-blue-800
           (tr [:condition/pre-consultation])])
        (when (and item->attachment-required? (item->attachment-required? item))
          [:span.text-xs.font-semibold.px-2.py-0.5.rounded.bg-blue-200.text-blue-800
           {:data-cy "condition-attachment-required-tag"}
           (tr [:condition/attachment-required-tag])])
        (when reordering?
          [:div.ml-auto
           (cc/icon {:height      "14px"
                     :icon        i/draggable
                     :description (tr [:reorder])})])]
       (when @*expanded?
         [:<>
          [:div.col-span-3.md:col-span-6.max-w-screen-md
           (ui-rt/editor-legacy {:initial-value      (-> item item->body)
                                 :disable-read-more? true
                                 :class              "mb-2"})]
          (when item->approval
            (when-let [approval (item->approval item)]
              [:<>
               [:div.col-span-3.md:col-span-6.max-w-screen-md
                [ui-sep/horizontal]]
               [:div.col-span-3.flex.items-center.select-none
                [:div.bold (tr [:condition/approval])]]
               [:div.col-span-2 (:user approval) ", " (-> (:ts approval) date-util/date-time)]
               [:div.col-span-1.flex.items-center
                [:span.font-bold.uppercase (tr [(:result approval)])]
                (cc/icon {:icon        (approval-result->icon (:result approval))
                          :description ""
                          :class-name  "ml-2"
                          :fill        (approval-result->color (:result approval))})]]))
          attachments-section
          [:div.col-span-4.flex.items-center
           (when (and can-close? on-close)
             [:div (cc/checkbox {:id         (str "check-condition-" id)
                                 :data-cy    "check-condition"
                                 :class-name "mr-4"
                                 :label-text (tr [:condition/signed-off])
                                 :checked    item-closed?
                                 :disabled   (boolean (and (not item-closed?) attachment-required-but-missing?))
                                 :on-change  #(on-close id)})])
           (when (and can-close? (not item-closed?) attachment-required-but-missing?)
             [:div.text-xs.text-ui-red-60 {:data-cy "condition-attachment-required-warning"}
              (tr [:condition/attachment-required-warning])])
           (when item-closed?
             [:div.relative.text-xs.mt-1 {:style {:top "-1px"}} (tr [:condition/closed]) " "
              [:span.font-bold (-> item item->closed-date)]
              " " (tr [:condition/by]) " "
              [:span.font-bold (-> item item->closed-by)]])]
          (when (and can-edit? on-edit)
            [:div.col-start-1.col-end-4.md:col-start-6.md:col-end-7.flex.justify-end
             (cc/button {:size     :small
                         :on-click #(on-edit id)}
                        (tr [:edit]))])])])))

(defn- edit-title
  [{:keys [tr id get-field set-field]}]
  [ui/text-input {:label-text (tr [:condition/description])
                  :id         "new-condition-description"
                  :value      (get-field id :application.condition/title)
                  :on-change  #(set-field id :application.condition/title (futil/tval %))}])

(defn- select-template
  [{:keys [tr id insert-template templates]}]
  (cc/select
    {:id         "template"
     :label-text (tr [:condition/template])
     :value      :none
     :on-change  (fn [e]
                   (when-let [selected (-> e futil/tval futil/parse-int)]
                     (insert-template id (p/find-first templates {:db/id selected}))))}
    [ui/placeholder-select-item tr :none]
    (futil/for-frag [template templates]
      (cc/select-item {:text  (:application.condition.template/title template)
                       :value (:db/id template)}))))

(defn- edit-body
  [{:keys [tr id get-field set-field default-group-id]}]
  [ui-rt/editor-legacy
   {:label-text         (tr [:condition/text-preview])
    :id                 (or (<sub [::condition-edit-key]) "new-condition-text-preview")
    :initial-value      (get-field id :application.condition/body)
    :on-change          #(set-field id :application.condition/body %)
    :authority-group-id default-group-id}])

(def condition-editor-container-layout
  :div.grid.grid-cols-4.gap-4)

(defn condition-editor-container [{:keys [creating-new? tr]} & children]
  (if creating-new?
    [ui-add/add-widget
     {:title (tr [:condition/add])}
     (into [condition-editor-container-layout]
           children)]
    (into [condition-editor-container-layout
           {:class "mb-4 p-4 bg-gray-200 border-l-4 border-solid border-ui-yellow"}]
          children)))

(defn can-edit-due-date? [{:keys [id creating-new? condition-instance?]} due-dates-enabled? manual-inputs? get-field]
  (let [published? (= (get-field id :domain/state) :application.condition.state/published)]
    (and condition-instance?
         due-dates-enabled?
         (or (and (not creating-new?) published?)
             manual-inputs?))))

(defn condition-editor-view
  [{:keys [id default-group-id tr save-tr-key
           loading? creating-new? can-save? can-delete?
           get-field set-field on-save on-cancel on-delete
           templates insert-template show-attachment-required?]
    :or   {loading? false}                                      :as ctx}]
  (let [due-dates-enabled? (<sub [::condition-due-dates-enabled? default-group-id])
        manual-inputs?     (<sub [::condition-due-date-manual-inputs?])]
    [condition-editor-container
     (util/mmap creating-new? tr)
     [:div.col-span-4.md:col-span-2
      [edit-title (util/mmap tr id get-field set-field)]]
     (when (get-field id :application.condition/addressed-to)
       [:div.col-span-2.md:col-span-1.flex.flex-col
        [:div.bx--label (tr [:condition/sign-off-dept])]
        [:div.mt-2 (:group/name (get-field id :application.condition/addressed-to))]])
     [:div.contained-select
      {:class (if (get-field id :application.condition/addressed-to)
                "col-span-2 md:col-span-1"
                "col-span-4 md:col-span-2")}
      (when (and (<sub [::default-conditions-enabled-for-group? default-group-id])
                 insert-template)
        [select-template (util/mmap tr id templates insert-template)])]
     (when (can-edit-due-date? ctx due-dates-enabled? manual-inputs? get-field)
       [:div.col-span-4.md:col-span-2
        [ui/date-picker {:label-text (tr [:condition/due-date])
                         :callback   #(set-field id :application.condition/due-date %)
                         :value      (get-field id :application.condition/due-date)}]])
     [:div.col-span-4
      [edit-body (util/mmap tr id get-field set-field default-group-id)]]
     (when show-attachment-required?
       [:div.col-span-4
        (cc/checkbox {:id         (str "condition-attachment-required-" id)
                      :data-cy    "condition-attachment-required"
                      :label-text (tr [:condition/attachment-required])
                      :checked    (boolean (get-field id :application.condition/attachment-required?))
                      :on-change  #(set-field id :application.condition/attachment-required? %)})])
     [:div.col-span-4.sm:col-span-1
      (when (and (not creating-new?) on-delete)
        [ui/modal-confirmation-button
         {:tr                  tr
          :id                  (str "delete-" id)
          :modal-title         (tr [:condition/delete-condition])
          :trigger-button-opts {:disabled (not can-delete?)
                                :kind     :danger}
          :trigger-button-text (tr [:delete])
          :no-button-text      (tr [:cancel])
          :yes-button-text     (tr [:delete])
          :on-yes-click        on-delete}
         [:p (tr [:condition/delete-body])]])]
     [:div.col-span-4.sm:col-span-3.flex.justify-end.gap-x-2
      (cc/button
        {:kind     :ghost
         :disabled loading?
         :on-click on-cancel}
        (tr [:cancel]))
      (cc/button
        {:disabled (not can-save?)
         :on-click on-save}
        (if loading?
          (tr [:saving])
          (tr [save-tr-key])))]]))
